import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin, { Draggable } from "@fullcalendar/interaction"

export default class extends Controller {
  static targets = [
    "calendar",
    "form",
    "scheduleId",
    "jobId",
    "scheduledStart",
    "duration",
    "keyCollection",
    "assigned",
    "title",
    "property",
    "agency",
    "status",
    "statusBanner",
    "showAllHours",
    "jumpDate",
    "routingButton",
    "legendItem",
    "unscheduledList",
    "fieldUsersToggle",
    "fieldUsersPanel",
    "fieldUsersToggleIcon",
    "jobSearch",
    "jobIdDisplay"
  ]

  static values = {
    eventsUrl: String,
    createUrl: String,
    updateUrl: String,
    slotMinTime: String,
    slotMaxTime: String,
    showAllHours: Boolean,
    fieldUsers: Array,
    routingUrl: String,
    timeZone: String,
    accountId: String,
    userId: String,
    targetDate: String
  }

  connect() {
    if (!this.hasCalendarTarget) return
    const initialView = this.storedView() || "timeGridWeek"
    const initialDate = this.storedDate()

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: initialView,
      initialDate: initialDate,
      timeZone: this.timeZoneValue || "local",
      slotMinTime: this.calendarStartTime(),
      slotMaxTime: this.calendarEndTime(),
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "timeGridDay,timeGridWeek,dayGridMonth"
      },
      eventTextColor: "#000",
      editable: true,
      selectable: false,
      events: this.eventsUrlValue,
      eventDataTransform: this.transformEvent.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventDrop: this.handleEventDrop.bind(this),
      eventResize: this.handleEventDrop.bind(this),
      eventDidMount: this.handleEventDidMount.bind(this),
      datesSet: this.handleDatesSet.bind(this),
      droppable: true,
      eventReceive: this.handleExternalReceive.bind(this)
    })

    this.calendar.render()
    if (this.targetDateValue) {
      this.calendar.changeView("timeGridDay", this.targetDateValue)
    }
    this.calendar.updateSize()
    window.requestAnimationFrame(() => {
      if (this.calendar) this.calendar.updateSize()
    })
    setTimeout(() => {
      if (this.calendar) this.calendar.updateSize()
    }, 300)
    this.bindExternalDraggable()
    this.bindShowAllHoursToggle()
    this.bindLegendSelection()
    this.syncFieldUsersAccordion()
    if (this.calendar && this.calendar.refetchEvents) {
      this.calendar.refetchEvents()
    }
  }

  handleEventClick(info) {
    const event = info.event
    this.populateForm(event)
  }

  handleEventDrop(info) {
    const event = info.event
    const payload = {
      schedule: {
        scheduled_start: event.start ? event.start.toISOString() : null,
        scheduled_end: event.end ? event.end.toISOString() : null,
        duration_minutes: this.durationFromEvent(event)
      }
    }
    this.sendUpdate(event.id, payload)
    this.populateForm(event)
  }

  selectUnscheduled(event) {
    if (!this.canUseForm()) return
    const button = event.currentTarget
    const jobId = button.dataset.jobId
    const jobTitle = button.dataset.jobTitle
    const propertyName = button.dataset.propertyName
    const agencyName = button.dataset.agencyName

    this.getTarget("scheduleId").value = ""
    this.getTarget("jobId").value = jobId
    this.getTarget("title").textContent = jobTitle
    this.getTarget("property").textContent = propertyName
    this.getTarget("agency").textContent = agencyName || "-"
    this.getTarget("status").textContent = "Unscheduled"
    const scheduledStart = this.getTarget("scheduledStart")
    const duration = this.getTarget("duration")
    scheduledStart.value = this.defaultScheduledStart()
    duration.value = duration.value || 60
  }

  toggleFieldUsers(event) {
    event.preventDefault()
    if (!this.hasFieldUsersPanelTarget) return
    this.fieldUsersPanelTarget.classList.toggle("show")
    this.syncFieldUsersAccordion()
  }

  syncFieldUsersAccordion() {
    if (!this.hasFieldUsersPanelTarget || !this.hasFieldUsersToggleTarget) return
    const expanded = this.fieldUsersPanelTarget.classList.contains("show")
    this.fieldUsersToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    this.fieldUsersToggleTarget.classList.toggle("collapsed", !expanded)
    if (this.hasFieldUsersToggleIconTarget) {
      this.fieldUsersToggleIconTarget.textContent = expanded ? "−" : "+"
    }
  }

  submit(event) {
    event.preventDefault()
    if (!this.canUseForm()) return
    if (this.assignedEmpty()) {
      this.showStatusBanner("Select at least one field user to schedule a job.")
      return
    }

    const scheduleId = this.getTarget("scheduleId").value
    const payload = {
      schedule: {
        scheduled_start: this.getTarget("scheduledStart").value,
        duration_minutes: this.getTarget("duration").value,
        key_collection_required: this.getTarget("keyCollection").checked
      },
      assigned_membership_ids: Array.from(this.getTarget("assigned").selectedOptions).map(option => option.value),
      job_id: this.getTarget("jobId").value
    }

    if (scheduleId) {
      this.sendUpdate(scheduleId, payload)
    } else {
      this.sendCreate(payload)
    }
  }

  populateForm(event) {
    if (!this.canUseForm()) return
    const props = event.extendedProps

    this.getTarget("scheduleId").value = event.id
    this.getTarget("jobId").value = props.job_id
    this.getTarget("title").textContent = event.title
    if (this.hasJobIdDisplayTarget) this.jobIdDisplayTarget.textContent = "JOB ID: " + props.job_id
    this.getTarget("property").textContent = props.property_name
    this.getTarget("agency").textContent = props.agency_name || "-"
    this.getTarget("status").textContent = props.status || "-"
    this.getTarget("scheduledStart").value = this.toLocalDatetime(event.start)
    this.getTarget("duration").value = props.duration_minutes || this.durationFromEvent(event)
    this.getTarget("keyCollection").checked = props.key_collection_required

    const assignedSelect = this.getTarget("assigned")
    const assignedIds = props.assigned_membership_ids || []
    Array.from(assignedSelect.options).forEach(option => {
      option.selected = assignedIds.includes(parseInt(option.value, 10))
    })
  }

  sendUpdate(id, payload) {
    fetch(this.updateUrlValue + "/" + id, this.requestOptions("PATCH", payload))
      .then(response => response.json())
      .then(() => {
        this.calendar.refetchEvents()
        this.showStatusBanner("Schedule updated. Routing will reflect the latest changes.")
        this.clearForm()
      })
      .catch(() => {})
  }

  sendCreate(payload) {
    fetch(this.createUrlValue, this.requestOptions("POST", payload))
      .then(response => response.json())
      .then(() => {
        this.calendar.refetchEvents()
        if (payload.job_id) {
          this.removeUnscheduled(payload.job_id)
        }
        this.showStatusBanner("Schedule created. Routing will reflect the latest changes.")
        this.clearForm()
      })
      .catch(() => {})
  }

  requestOptions(method, payload) {
    const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
    return {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify(payload)
    }
  }

  durationFromEvent(event) {
    if (event.start && event.end) {
      return Math.max(5, Math.round((event.end - event.start) / 60000))
    }
    return 60
  }

  toLocalDatetime(date) {
    if (!date) return ""
    const pad = value => value.toString().padStart(2, "0")
    return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) + " " + pad(date.getHours()) + ":" + pad(date.getMinutes())
  }

  defaultScheduledStart() {
    const now = new Date()
    now.setMinutes(0)
    now.setSeconds(0)
    now.setMilliseconds(0)
    now.setHours(now.getHours() + 1)
    return this.toLocalDatetime(now)
  }

  showStatusBanner(message) {
    if (!this.hasStatusBannerTarget) return

    this.statusBannerTarget.textContent = message
    this.statusBannerTarget.classList.remove("d-none")
    clearTimeout(this.statusBannerTimeout)
    this.statusBannerTimeout = setTimeout(() => {
      this.statusBannerTarget.classList.add("d-none")
    }, 6000)
  }

  clearForm() {
    if (!this.canUseForm()) return
    this.getTarget("scheduleId").value = ""
    this.getTarget("jobId").value = ""
    this.getTarget("title").textContent = "Select a job"
    if (this.hasJobIdDisplayTarget) this.jobIdDisplayTarget.textContent = ""
    this.getTarget("property").textContent = "-"
    this.getTarget("agency").textContent = "-"
    this.getTarget("status").textContent = "-"
    this.getTarget("scheduledStart").value = ""
    this.getTarget("duration").value = ""
    this.getTarget("keyCollection").checked = false
    const assignedSelect = this.getTarget("assigned")
    Array.from(assignedSelect.options).forEach(option => {
      option.selected = false
    })
  }

  bindExternalDraggable() {
    if (!this.hasUnscheduledListTarget) return
    if (this.unscheduledDraggable) return

    this.unscheduledDraggable = new Draggable(this.unscheduledListTarget, {
      itemSelector: "[data-unscheduled-job]",
      eventData: element => ({
        title: element.dataset.jobTitle,
        extendedProps: {
          job_id: parseInt(element.dataset.jobId, 10),
          property_name: element.dataset.propertyName,
          agency_name: element.dataset.agencyName
        }
      })
    })
  }

  handleExternalReceive(info) {
    const jobId = info.event.extendedProps.job_id
    if (!jobId) return
    if (this.assignedEmpty()) {
      this.showStatusBanner("Select at least one field user to schedule a job.")
      info.event.remove()
      return
    }

    const payload = {
      schedule: {
        scheduled_start: info.event.start ? info.event.start.toISOString() : null,
        duration_minutes: this.durationFromEvent(info.event)
      },
      job_id: jobId
    }

    this.sendCreate(payload)
    if (info.draggedEl) {
      info.draggedEl.remove()
    }
    info.event.remove()
  }

  handleEventDidMount(info) {
    this.applyEventColor(info)
  }

  applyEventColor(info) {
    const assigned = info.event.extendedProps.assigned_membership_ids || []
    if (!assigned.length) return
    const colorMap = this.fieldUserColorMap()
    const color = colorMap[assigned[0]]
    if (color) {
      info.el.style.backgroundColor = color
      info.el.style.borderColor = color
    }
  }

  applyEventFilter(info) {
    const selected = this.selectedFieldUserIds()
    if (!selected.length) {
      info.el.style.display = "none"
      return
    }
    const assigned = info.event.extendedProps.assigned_membership_ids || []
    const matches = assigned.some(id => selected.includes(id))
    info.el.style.display = matches ? "" : "none"
  }

  removeUnscheduled(jobId) {
    if (!this.hasUnscheduledListTarget) return
    const item = this.unscheduledListTarget.querySelector(`[data-job-id="${jobId}"]`)
    if (item) item.remove()
  }

  assignedEmpty() {
    if (!this.hasAssignedTarget) return true
    return Array.from(this.assignedTarget.selectedOptions).length === 0
  }

  bindFieldUserFilter() {
  }

  bindLegendSelection() {
    const items = this.legendItems()
    if (!items.length) return
    const stored = this.storedFieldUserSelection()
    if (stored.length) {
      items.forEach(item => {
        const userId = parseInt(item.dataset.userId, 10)
        item.classList.toggle("is-selected", stored.includes(userId))
      })
    } else {
      items.forEach(item => {
        item.classList.add("is-selected")
      })
    }
    this.syncLegendStyles()
  }

  selectedFieldUserIds() {
    const items = this.legendItems()
    if (!items.length) return []
    return items
      .filter(item => item.classList.contains("is-selected"))
      .map(item => parseInt(item.dataset.userId, 10))
  }

  toggleUserFilter(event) {
    const button = event.currentTarget
    button.classList.toggle("is-selected")
    this.syncLegendStyles()
    this.persistFieldUserSelection()
    if (this.calendar && this.calendar.refetchEvents) {
      this.calendar.refetchEvents()
    }
  }

  selectAllUsers() {
    const items = this.legendItems()
    items.forEach(item => item.classList.add("is-selected"))
    this.syncLegendStyles()
    this.persistFieldUserSelection()
    if (this.calendar && this.calendar.refetchEvents) {
      this.calendar.refetchEvents()
    }
  }

  hideAllUsers() {
    const items = this.legendItems()
    items.forEach(item => item.classList.remove("is-selected"))
    this.syncLegendStyles()
    this.persistFieldUserSelection()
    if (this.calendar && this.calendar.refetchEvents) {
      this.calendar.refetchEvents()
    }
  }

  // Picking a group selects exactly its members in the legend (mutually
  // exclusive with manual user selection). Picking the blank option leaves
  // the current selection intact.
  selectGroup(event) {
    const select = event.currentTarget
    const option = select.options[select.selectedIndex]
    if (!option || !option.value) return

    const memberIdsAttr = option.dataset.membershipIds || ""
    const memberIds = memberIdsAttr.split(",").map(s => s.trim()).filter(Boolean)
    const items = this.legendItems()
    items.forEach(item => {
      const uid = (item.dataset.userId || "").toString()
      item.classList.toggle("is-selected", memberIds.includes(uid))
    })
    this.syncLegendStyles()
    this.persistFieldUserSelection()
    if (this.calendar && this.calendar.refetchEvents) {
      this.calendar.refetchEvents()
    }
  }

  transformEvent(eventData) {
    const selected = this.selectedFieldUserIds()
    if (!selected.length) return false

    const rawAssigned =
      (eventData.extendedProps && eventData.extendedProps.assigned_membership_ids) ||
      eventData.assigned_membership_ids ||
      []
    const assigned = Array.isArray(rawAssigned) ? rawAssigned.map(id => parseInt(id, 10)) : []
    const matches = assigned.some(id => selected.includes(id))
    return matches ? eventData : false
  }

  persistFieldUserSelection() {
    const selectedIds = this.selectedFieldUserIds()
    try {
      localStorage.setItem(this.fieldUserStorageKey(), JSON.stringify(selectedIds))
    } catch (_error) {}
  }

  storedFieldUserSelection() {
    try {
      const raw = localStorage.getItem(this.fieldUserStorageKey())
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.map(id => parseInt(id, 10)).filter(Number.isFinite) : []
    } catch (_error) {
      return []
    }
  }

  fieldUserStorageKey() {
    const accountId = this.accountIdValue || "global"
    const userId = this.userIdValue || "anon"
    return `schedule:field-users:${userId}:${accountId}`
  }

  fieldUserColorMap() {
    if (!this.fieldUsersValue) return {}
    return this.fieldUsersValue.reduce((map, entry) => {
      map[entry.id] = entry.color
      return map
    }, {})
  }

  bindShowAllHoursToggle() {
    if (!this.hasShowAllHoursTarget) return
    this.calendar.setOption("slotMinTime", this.calendarStartTime())
    this.calendar.setOption("slotMaxTime", this.calendarEndTime())
    this.showAllHoursTarget.addEventListener("change", () => {
      this.calendar.setOption("slotMinTime", this.calendarStartTime())
      this.calendar.setOption("slotMaxTime", this.calendarEndTime())
    })
  }

  openRouting(event) {
    event.preventDefault()
    if (!this.calendar || !this.routingUrlValue) return
    if (this.calendar.view?.type !== "timeGridDay") return

    const date = this.calendar.getDate()
    const dateString = date.toISOString().slice(0, 10)
    const selected = this.selectedFieldUserIds()
    const fieldUserId = selected.length === 1 ? selected[0] : ""
    const params = new URLSearchParams()
    params.append("date", dateString)
    if (fieldUserId) params.append("field_user_id", fieldUserId)

    window.location.href = this.routingUrlValue + "?" + params.toString()
  }

  jumpToDate() {
    if (!this.calendar || !this.hasJumpDateTarget) return
    const raw = this.jumpDateTarget.value
    if (!raw) return
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return
    this.calendar.gotoDate(date)
  }

  searchJobId() {
    if (!this.calendar || !this.hasJobSearchTarget) return
    const raw = this.jobSearchTarget.value.trim()
    if (!raw) return
    const jobId = parseInt(raw, 10)
    if (Number.isNaN(jobId)) return

    const events = this.calendar.getEvents()
    const found = events.find(e => e.extendedProps && parseInt(e.extendedProps.job_id, 10) === jobId)

    if (!found || !found.start) {
      this.showStatusBanner(`Job #${jobId} was not found in the schedule.`)
      return
    }

    this.calendar.changeView("timeGridDay", found.start)
    this.populateForm(found)
  }

  handleDatesSet(info) {
    const viewType = info?.view?.type
    if (!viewType) return
    if (!this.allowedViews().includes(viewType)) return
    try {
      localStorage.setItem(this.viewStorageKey(), viewType)
      if (info?.view?.currentStart) {
        localStorage.setItem(this.dateStorageKey(), info.view.currentStart.toISOString())
      }
    } catch (_) {}
    this.updateRoutingButtonState(viewType)
  }

  storedView() {
    try {
      const value = localStorage.getItem(this.viewStorageKey())
      if (this.allowedViews().includes(value)) return value
    } catch (_) {}
    return null
  }

  storedDate() {
    try {
      const value = localStorage.getItem(this.dateStorageKey())
      if (value) return value
    } catch (_) {}
    return null
  }

  allowedViews() {
    return [ "timeGridDay", "timeGridWeek", "dayGridMonth" ]
  }

  viewStorageKey() {
    return `scheduleCalendarView:${window.location.pathname}`
  }

  dateStorageKey() {
    return `scheduleCalendarDate:${window.location.pathname}`
  }

  updateRoutingButtonState(viewType) {
    if (!this.hasRoutingButtonTarget) return
    const enabled = viewType === "timeGridDay"
    this.routingButtonTarget.disabled = !enabled
    this.routingButtonTarget.classList.toggle("disabled", !enabled)
  }

  calendarStartTime() {
    if (this.showAllHoursEnabled()) return "00:00:00"
    return this.slotMinTimeValue || "07:00:00"
  }

  calendarEndTime() {
    if (this.showAllHoursEnabled()) return "24:00:00"
    return this.slotMaxTimeValue || "19:00:00"
  }

  showAllHoursEnabled() {
    if (this.hasShowAllHoursTarget) {
      return this.showAllHoursTarget.checked
    }
    return !!this.showAllHoursValue
  }

  canUseForm() {
    if (!this.targets.find("scheduleId") || !this.targets.find("jobId") || !this.targets.find("scheduledStart") || !this.targets.find("duration") || !this.targets.find("assigned")) {
      this.showStatusBanner("Schedule details are not available on this page.")
      return false
    }
    return true
  }

  getTarget(name) {
    const target = this.targets.find(name)
    if (!target) throw new Error(`Missing target element ${name}`)
    return target
  }

  legendItems() {
    if (this.hasLegendItemTarget) {
      return this.legendItemTargets
    }
    return Array.from(this.element.querySelectorAll("[data-user-id]"))
  }

  syncLegendStyles() {
    const items = this.legendItems()
    items.forEach(item => {
      item.classList.toggle("is-muted", !item.classList.contains("is-selected"))
    })
  }
}
